/**
 * Notification playback engine — queued, non-overlapping segment player.
 *
 * Loads the pack via manifest only. Supports master mute/volume, debounce,
 * missing-token fallback, autoplay unlock, and critical supersede of routine.
 */

import {
  indexReadyAssets,
  loadNotificationPackManifest,
  NOTIFICATION_PACK_BASE,
  type NotificationPackManifest,
  type ResolvedNotificationAsset,
} from './notification-audio-pack';
import {
  materializePlanUrls,
  planNotificationPlayback,
  type NotificationSoundEvent,
  type PlaybackPlan,
} from './notification-events';
import {
  recallNotificationSoundSettings,
  soundAudible,
  type NotificationSoundSettings,
} from './notification-sound-settings';

export const DEFAULT_DEBOUNCE_MS = 2_500;

export interface QueuedPlaybackJob {
  readonly id: string;
  readonly urls: readonly string[];
  readonly critical: boolean;
  readonly label: string;
}

export interface NotificationPlayerStatus {
  readonly ready: boolean;
  readonly unlocked: boolean;
  readonly queueLength: number;
  readonly playing: boolean;
  readonly lastError: string | null;
  readonly packId: string | null;
}

type AudioFactory = (url: string) => HTMLAudioElement;

export interface NotificationPlayerOptions {
  readonly fetchImpl?: typeof fetch;
  readonly createAudio?: AudioFactory;
  readonly debounceMs?: number;
  readonly now?: () => number;
  readonly packBase?: string;
}

function defaultAudio(url: string): HTMLAudioElement {
  return new Audio(url);
}

/**
 * Browser notification player. Construct once per desk session; call
 * `ensureLoaded` before playing, and `unlock` from a user gesture.
 */
export class NotificationPlayer {
  private settings: NotificationSoundSettings;
  private assets: ReadonlyMap<string, ResolvedNotificationAsset> = new Map();
  private manifest: NotificationPackManifest | null = null;
  private queue: QueuedPlaybackJob[] = [];
  private playing = false;
  private current: HTMLAudioElement | null = null;
  private unlocked = false;
  private lastError: string | null = null;
  private debounceUntil = new Map<string, number>();
  private jobSeq = 0;
  private readonly createAudio: AudioFactory;
  private readonly fetchImpl: typeof fetch;
  private readonly debounceMs: number;
  private readonly now: () => number;
  private readonly packBase: string;
  private loadPromise: Promise<boolean> | null = null;

  constructor(options: NotificationPlayerOptions = {}) {
    this.settings = recallNotificationSoundSettings();
    this.createAudio = options.createAudio ?? defaultAudio;
    this.fetchImpl = options.fetchImpl ?? fetch;
    this.debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this.now = options.now ?? (() => Date.now());
    this.packBase = options.packBase ?? NOTIFICATION_PACK_BASE;
  }

  getStatus(): NotificationPlayerStatus {
    return {
      ready: this.manifest !== null && this.assets.size > 0,
      unlocked: this.unlocked,
      queueLength: this.queue.length,
      playing: this.playing,
      lastError: this.lastError,
      packId: this.manifest?.packId ?? null,
    };
  }

  getSettings(): NotificationSoundSettings {
    return this.settings;
  }

  setSettings(settings: NotificationSoundSettings): void {
    this.settings = settings;
    if (!soundAudible(settings)) this.clearQueue({ stopCurrent: true });
  }

  /** Mark autoplay unlocked after a user gesture (click/key). */
  unlock(): void {
    this.unlocked = true;
    void this.pump();
  }

  async ensureLoaded(): Promise<boolean> {
    if (this.manifest !== null && this.assets.size > 0) return true;
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = (async () => {
      const manifest = await loadNotificationPackManifest(this.fetchImpl);
      if (manifest === null) {
        this.lastError = 'Notification audio pack failed to load';
        return false;
      }
      this.manifest = manifest;
      this.assets = indexReadyAssets(manifest, this.packBase);
      this.lastError = null;
      return this.assets.size > 0;
    })();
    try {
      return await this.loadPromise;
    } finally {
      this.loadPromise = null;
    }
  }

  /** Inject a preloaded manifest (tests). */
  loadFromManifest(manifest: NotificationPackManifest): void {
    this.manifest = manifest;
    this.assets = indexReadyAssets(manifest, this.packBase);
    this.lastError = null;
  }

  /**
   * Enqueue a Phase 1 event. Respects mute, debounce, and critical supersede.
   * Returns whether anything was queued.
   */
  async enqueueEvent(event: NotificationSoundEvent): Promise<boolean> {
    await this.ensureLoaded();
    if (!soundAudible(this.settings)) return false;
    if (this.isDebounced(event.debounceKey)) return false;

    const plan = planNotificationPlayback(event, this.settings);
    return this.enqueuePlan(plan, event.debounceKey, `${event.kind} · Worker ${event.workerNumber}`);
  }

  /** Preview / test a PlaybackPlan (settings panel). */
  async enqueuePlan(
    plan: PlaybackPlan,
    debounceKey: string | null = null,
    label = 'preview',
  ): Promise<boolean> {
    await this.ensureLoaded();
    if (!soundAudible(this.settings)) return false;
    if (plan.mode === 'off' || plan.tokenIds.length === 0) {
      // Still allow explicit beep-only plans with tokenIds.
      if (plan.tokenIds.length === 0) return false;
    }
    if (debounceKey !== null && this.isDebounced(debounceKey)) return false;

    const { urls } = materializePlanUrls(plan, this.assets);
    if (urls.length === 0) {
      this.lastError = 'Missing notification audio tokens';
      return false;
    }

    if (plan.critical) this.clearQueue({ stopCurrent: true });

    if (debounceKey !== null) {
      this.debounceUntil.set(debounceKey, this.now() + this.debounceMs);
    }

    this.jobSeq += 1;
    this.queue.push({
      id: `job-${this.jobSeq}`,
      urls,
      critical: plan.critical,
      label,
    });
    void this.pump();
    return true;
  }

  /** Play one or more manifest asset ids in order (join preview / test buttons). */
  async playTokenIds(
    tokenIds: readonly string[],
    options: { readonly critical?: boolean; readonly label?: string; readonly debounceKey?: string | null } = {},
  ): Promise<boolean> {
    const plan: PlaybackPlan = {
      mode: tokenIds.some((id) => id.startsWith('sfx-')) && tokenIds.length === 1 ? 'beep' : 'voice',
      severity: options.critical ? 'critical' : 'routine',
      critical: Boolean(options.critical),
      tokenIds,
      fallbackSfxId: 'sfx-attention',
    };
    return this.enqueuePlan(plan, options.debounceKey ?? null, options.label ?? 'tokens');
  }

  clearQueue(options: { readonly stopCurrent?: boolean } = {}): void {
    this.queue = [];
    if (options.stopCurrent && this.current) {
      try {
        this.current.pause();
        this.current.src = '';
      } catch {
        // ignore
      }
      this.current = null;
      this.playing = false;
    }
  }

  private isDebounced(key: string): boolean {
    const until = this.debounceUntil.get(key);
    if (until === undefined) return false;
    if (this.now() >= until) {
      this.debounceUntil.delete(key);
      return false;
    }
    return true;
  }

  private async pump(): Promise<void> {
    if (this.playing) return;
    if (!this.unlocked) return;
    if (!soundAudible(this.settings)) {
      this.clearQueue({ stopCurrent: true });
      return;
    }
    const job = this.queue.shift();
    if (!job) return;
    this.playing = true;
    try {
      for (const url of job.urls) {
        if (!soundAudible(this.settings)) break;
        await this.playOne(url);
      }
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : 'Playback failed';
    } finally {
      this.playing = false;
      this.current = null;
      void this.pump();
    }
  }

  private playOne(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let audio: HTMLAudioElement;
      try {
        audio = this.createAudio(url);
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Audio create failed'));
        return;
      }
      this.current = audio;
      audio.volume = Math.min(1, Math.max(0, this.settings.volume));
      const finish = () => {
        audio.onended = null;
        audio.onerror = null;
        resolve();
      };
      audio.onended = finish;
      audio.onerror = () => {
        audio.onended = null;
        audio.onerror = null;
        // Skip broken clip; do not abort the whole queue.
        resolve();
      };
      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult.catch((error: unknown) => {
          // Autoplay block — stay locked until unlock().
          this.unlocked = false;
          this.lastError = error instanceof Error ? error.message : 'Autoplay blocked';
          finish();
        });
      }
    });
  }
}

/** Singleton for the command desk (lazy). */
let sharedPlayer: NotificationPlayer | null = null;

export function getSharedNotificationPlayer(): NotificationPlayer {
  if (sharedPlayer === null) sharedPlayer = new NotificationPlayer();
  return sharedPlayer;
}

/** Test helper — replace/clear the shared instance. */
export function resetSharedNotificationPlayer(player: NotificationPlayer | null = null): void {
  sharedPlayer = player;
}
